import type { JSX } from 'react';
import { useState } from 'react';
import { isQuestionOption } from '@logistics/contracts';
import type { WireScheduledPrompt } from '@logistics/contracts';
import { failureFrom } from '@/lib/result';
import { cn } from '@/lib/utils';
import type { Me } from '../auth/use-me';
import { button, card, monoLabel } from '../shell/look';
import { PageHead } from '../shell/page-head';
import type { Route } from '../shell/routes';
import { EmptyState, ErrorState, LoadingState } from '../shell/states';
import { navigate } from '../shell/use-route';
import { ScheduleEditor } from './schedule-editor';
import { emptySchedule, formOf } from './schedule-form';
import { SCHEDULE_COLUMNS, ScheduleRow } from './schedule-row';
import { ScheduleRuns } from './schedule-runs';
import { useScheduleAction, useSchedules } from './use-schedules';
import type { ScheduleList } from './use-schedules';

/**
 * Scheduled prompts. `#/schedules/add?prompt=…&scope=…` opens the editor
 * prefilled — the chat's "Schedule this" lands here. Admin only.
 */
export function SchedulesPage({ route, me }: { route: Route; me: Me }): JSX.Element {
  const admin = me.role === 'admin';
  return (
    <section className="animate-rise">
      <PageHead
        eyebrow="AUTOMATION"
        title="Scheduled prompts"
        description="Prompts tested in chat, run on a timetable, and sent by Telegram or as a new web chat."
        action={
          admin ? (
            <button
              type="button"
              className={button.primary}
              onClick={() => navigate({ page: 'schedules', action: 'add' })}
            >
              + Schedule prompt
            </button>
          ) : undefined
        }
      />
      {admin ? (
        <ScheduleTable route={route} />
      ) : (
        <div className={card}>
          <EmptyState title="Only admins can schedule prompts" body="Ask an admin to set one up." />
        </div>
      )}
    </section>
  );
}

function ScheduleTable({ route }: { route: Route }): JSX.Element {
  const list = useSchedules();
  const action = useScheduleAction();
  const [runsOf, setRunsOf] = useState<WireScheduledPrompt | undefined>(undefined);

  if (list.isPending) return <LoadingState rows={4} />;
  if (list.isError) {
    return <ErrorState {...failureFrom(list.error)} onRetry={() => void list.refetch()} />;
  }
  const { schedules } = list.data;
  return (
    <div className={cn(card, 'overflow-x-auto')}>
      <div className="min-w-[820px]">
        <div className={cn(SCHEDULE_COLUMNS, monoLabel, 'border-line border-b py-3')}>
          <span>PROMPT</span>
          <span>WHEN</span>
          <span>TO</span>
          <span>NEXT RUN</span>
          <span>LAST</span>
          <span className="text-right">ACTIONS</span>
        </div>
        {schedules.length === 0 && (
          <div className="text-mute p-10 text-center text-sm">
            Nothing scheduled yet. Ask a question in Chat, and schedule it from there once the
            answer is right.
          </div>
        )}
        {schedules.map((schedule) => (
          <ScheduleRow
            key={schedule.id}
            schedule={schedule}
            busy={action.isPending && action.variables.schedule.id === schedule.id}
            onAction={(kind) => action.mutate({ schedule, kind })}
            onEdit={() =>
              navigate({ page: 'schedules', action: 'edit', query: { id: schedule.id } })
            }
            onRuns={() => setRunsOf(schedule)}
          />
        ))}
      </div>
      <EditorFromRoute route={route} data={list.data} />
      {runsOf !== undefined && (
        <ScheduleRuns schedule={runsOf} onClose={() => setRunsOf(undefined)} />
      )}
    </div>
  );
}

/** The editor the route asks for: a new one (maybe prefilled from chat), or one being edited. */
function EditorFromRoute({
  route,
  data,
}: {
  route: Route;
  data: ScheduleList;
}): JSX.Element | null {
  const close = (): void => navigate({ page: 'schedules' });
  if (route.action === 'add') {
    const scope = (route.query?.['scope'] ?? '').split(',').filter(isQuestionOption);
    const prompt = route.query?.['prompt'];
    return (
      <ScheduleEditor
        initial={emptySchedule(data.defaultTimezone, {
          ...(prompt === undefined ? {} : { prompt }),
          scope,
        })}
        timezones={data.timezones}
        onClose={close}
      />
    );
  }
  const editing = data.schedules.find((s) => s.id === route.query?.['id']);
  if (route.action === 'edit' && editing !== undefined) {
    return (
      <ScheduleEditor
        editing={editing}
        initial={formOf(editing)}
        timezones={data.timezones}
        onClose={close}
      />
    );
  }
  return null;
}
